import { Alert, Box, Button, FormControlLabel, Stack, Switch, Typography } from '@mui/material';

import { MapOverlayLiveStatus } from './CanvassingSessionMapOverlayRecords';
import { MAP_OVERLAY_COPY } from './canvassingSessionMapOverlay';
import type { UseCanvassingSessionMapOverlayResult } from './useCanvassingSessionMapOverlay';

export const CanvassingSessionMapOverlayStatus = ({
  overlay,
  gpsSettled,
  boundsAvailable,
}: {
  overlay: UseCanvassingSessionMapOverlayResult;
  gpsSettled: boolean;
  boundsAvailable: boolean;
}) => {
  const voterLayer = overlay.model.published.voters;
  const visitLayer = overlay.model.published.visits;
  const showVoterUnavailable = gpsSettled && !boundsAvailable;
  const announcement = overlayAnnouncement(overlay);

  return (
    <Stack spacing={1.5}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.5, sm: 2 }}>
        {overlay.showVotersToggle && (
          <FormControlLabel
            label={MAP_OVERLAY_COPY.votersLabel}
            control={(
              <Switch
                checked={overlay.votersToggle}
                onChange={event => overlay.setVotersToggle(event.target.checked)}
                slotProps={{ input: { 'aria-label': MAP_OVERLAY_COPY.votersLabel } }}
              />
            )}
          />
        )}
        <FormControlLabel
          label={MAP_OVERLAY_COPY.visitsLabel}
          control={(
            <Switch
              checked={overlay.visitsToggle}
              onChange={event => overlay.setVisitsToggle(event.target.checked)}
              slotProps={{ input: { 'aria-label': MAP_OVERLAY_COPY.visitsLabel } }}
            />
          )}
        />
      </Stack>
      <MapOverlayLiveStatus message={announcement} />
      {showVoterUnavailable && (
        <Typography variant="body2" color="text.secondary">{MAP_OVERLAY_COPY.votersUnavailable}</Typography>
      )}
      {overlay.model.generationStale && (
        <Alert severity="warning">{MAP_OVERLAY_COPY.stale}</Alert>
      )}
      <LayerResult
        label="Nearby voters"
        busy={overlay.votersLoading}
        visible={overlay.showVotersToggle && overlay.votersToggle && boundsAvailable}
        loadingMore={overlay.votersLoading && voterLayer.items.length > 0}
        empty={voterLayer.complete && voterLayer.items.length === 0 && !voterLayer.unavailable}
        emptyLabel={MAP_OVERLAY_COPY.noVoters}
        incomplete={voterLayer.error !== null}
        incompleteLabel={voterLayer.error?.message ?? MAP_OVERLAY_COPY.incompleteVoters}
        retryLabel={MAP_OVERLAY_COPY.retryVoters}
        onRetry={overlay.retryVoters}
      />
      <LayerResult
        label="Door visits on the map"
        busy={overlay.visitsLoading}
        visible={overlay.visitsToggle}
        loadingMore={overlay.visitsLoading && visitLayer.items.length > 0}
        empty={visitLayer.complete && visitLayer.items.length === 0}
        emptyLabel={MAP_OVERLAY_COPY.noVisits}
        incomplete={visitLayer.error !== null}
        incompleteLabel={visitLayer.error?.message ?? MAP_OVERLAY_COPY.incompleteVisits}
        retryLabel={MAP_OVERLAY_COPY.retryVisits}
        onRetry={overlay.retryVisits}
      />
    </Stack>
  );
};

const overlayAnnouncement = (overlay: UseCanvassingSessionMapOverlayResult): string => {
  if (overlay.refreshing) return MAP_OVERLAY_COPY.refreshing;
  if (overlay.votersLoading && overlay.model.published.voters.items.length === 0) return MAP_OVERLAY_COPY.loadingVoters;
  if (overlay.visitsLoading && overlay.model.published.visits.items.length === 0) return MAP_OVERLAY_COPY.loadingVisits;
  if (overlay.votersLoading && overlay.model.published.voters.items.length > 0) return MAP_OVERLAY_COPY.loadingMore;
  if (overlay.visitsLoading && overlay.model.published.visits.items.length > 0) return MAP_OVERLAY_COPY.loadingMore;
  if (overlay.model.published.voters.error !== null) return overlay.model.published.voters.error.message;
  if (overlay.model.published.visits.error !== null) return overlay.model.published.visits.error.message;
  if (overlay.model.generationStale && !overlay.refreshing) return MAP_OVERLAY_COPY.stale;
  return '';
};

const LayerResult = ({
  label,
  busy,
  visible,
  loadingMore,
  empty,
  emptyLabel,
  incomplete,
  incompleteLabel,
  retryLabel,
  onRetry,
}: {
  label: string;
  busy: boolean;
  visible: boolean;
  loadingMore: boolean;
  empty: boolean;
  emptyLabel: string;
  incomplete: boolean;
  incompleteLabel: string;
  retryLabel: string;
  onRetry: () => void;
}) => {
  const showLoadingMore = visible && loadingMore;
  const showEmpty = visible && empty && !incomplete;
  if (!showLoadingMore && !showEmpty && !incomplete) return null;
  return (
    <Box role="region" aria-label={label} aria-busy={busy}>
      {showLoadingMore && <Typography variant="body2" color="text.secondary">{MAP_OVERLAY_COPY.loadingMore}</Typography>}
      {showEmpty && <Typography variant="body2" color="text.secondary">{emptyLabel}</Typography>}
      {incomplete && (
        <Alert severity="error" action={<Button color="inherit" size="small" onClick={onRetry}>{retryLabel}</Button>}>
          {incompleteLabel}
        </Alert>
      )}
    </Box>
  );
};
